import { useCallback, useRef, useState } from 'react';
import { useAppStore } from '@/state/store';
import { groupFilesByBaseName } from '@/lib/batchProcessFiles';

export function FileUpload() {
  const loadFileGroup = useAppStore((s) => s.loadFileGroup);
  const pushStatus = useAppStore((s) => s.pushStatus);
  const isProcessing = useAppStore((s) => s.isProcessing);
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFiles = useCallback(
    (fileList: FileList | null) => {
      if (!fileList || fileList.length === 0) return;
      const files = Array.from(fileList);

      const groups = groupFilesByBaseName(files);
      if (groups.length > 1) {
        pushStatus(
          'warning',
          `These files span ${groups.length} different models/sizes (${groups.map((g) => g.baseName).join(', ')}). This slot loads one model at a time — use the Library tab's batch import to process several at once.`,
        );
        return;
      }

      const hasPrimary = files.some((f) => /\.(c|tur)$/i.test(f.name));
      if (!hasPrimary) {
        pushStatus(
          'error',
          `No .C or .TUR file found among ${files.length > 1 ? 'these files' : `"${files[0].name}"`}. At least one is required to generate geometry.`,
        );
        return;
      }

      void loadFileGroup(files);
    },
    [loadFileGroup, pushStatus],
  );

  return (
    <div>
      <div className="sidebar-section-label">Upload Files</div>
      <div
        className={`dropzone${isDragging ? ' is-dragging' : ''}`}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setIsDragging(false);
          handleFiles(e.dataTransfer.files);
        }}
        role="button"
        tabIndex={0}
        aria-label="Upload Mark Lumber profile files for one model"
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click();
        }}
      >
        <svg className="dropzone-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M12 15V4M12 4L7.5 8.5M12 4l4.5 4.5"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M4 15v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <div className="dropzone-title">{isProcessing ? 'Processing...' : "Drop one model's files here"}</div>
        <div className="dropzone-subtitle">
          all 6 at once is fine (.DAT/.TUR/.U/.V/.SAN/.C) — .TUR is used for geometry
        </div>
        <input ref={inputRef} type="file" multiple onChange={(e) => handleFiles(e.target.files)} />
      </div>
    </div>
  );
}
